import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Package, ChefHat, DollarSign, TrendingDown, AlertTriangle, ArrowUpRight, Percent } from 'lucide-react';

export default function Dashboard() {
  const { axiosAuth, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosAuth().get('/dashboard');
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [axiosAuth]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  const kpis = data?.kpis || {};
  const currency = data?.currency || 'USD';

  const kpiCards = [
    { icon: Package, label: 'Ingredientes', value: kpis.total_ingredients || 0, color: 'text-gold' },
    { icon: ChefHat, label: 'Recetas', value: kpis.total_recipes || 0, color: 'text-gold' },
    { icon: Percent, label: 'Food Cost Prom.', value: `${kpis.avg_food_cost_pct || 0}%`, color: kpis.avg_food_cost_pct > 35 ? 'text-red-400' : 'text-emerald-400' },
    { icon: DollarSign, label: 'Costo Prom. Receta', value: `${currency} ${kpis.avg_recipe_cost?.toFixed(2) || '0.00'}`, color: 'text-white' },
    { icon: TrendingDown, label: 'Merma Promedio', value: `${kpis.avg_waste_pct || 0}%`, color: kpis.avg_waste_pct > 10 ? 'text-red-400' : 'text-emerald-400' },
    { icon: AlertTriangle, label: 'Costo x Mermas', value: `${currency} ${kpis.total_waste_cost?.toFixed(2) || '0.00'}`, color: 'text-amber-400' },
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Dashboard</p>
        <h2 className="font-heading text-3xl font-bold">Bienvenido, {user?.name?.split(' ')[0]}</h2>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kpi-grid">
        {kpiCards.map((kpi, i) => (
          <div key={i} className="p-6 bg-[#0A0A0A] border border-white/5 hover:border-gold/20 transition-colors" data-testid={`kpi-card-${i}`}>
            <div className="flex items-center justify-between mb-4">
              <kpi.icon size={18} className="text-gold/60" strokeWidth={1.5} />
              <ArrowUpRight size={14} className="text-white/20" />
            </div>
            <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Top Recipes */}
      {data?.top_recipes?.length > 0 && (
        <div className="bg-[#0A0A0A] border border-white/5 p-6" data-testid="top-recipes-section">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-4">Top Recetas por Food Cost</p>
          <div className="space-y-3">
            {data.top_recipes.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <span className="text-sm">{r.name}</span>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-white/40">{currency} {r.total_cost?.toFixed(2)}</span>
                  <span className={`text-xs font-mono ${r.food_cost_pct > 35 ? 'text-red-400' : 'text-emerald-400'}`}>{r.food_cost_pct?.toFixed(1)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Usage / Trial info */}
      {!data?.has_subscription && (
        <div className="bg-gold/5 border border-gold/20 p-6" data-testid="trial-info">
          <p className="text-gold text-sm font-semibold mb-2">Modo Prueba</p>
          <p className="text-xs text-white/50">
            Estas usando {user?.usage?.ingredients || 0}/{TRIAL_LIMITS.max_ingredients} ingredientes y {user?.usage?.recipes || 0}/{TRIAL_LIMITS.max_recipes} recetas.
            Suscribete para acceso ilimitado.
          </p>
        </div>
      )}
    </div>
  );
}

const TRIAL_LIMITS = { max_ingredients: 10, max_recipes: 1 };
